import ModelAlgorithmEnum from '../types/ModelAlgorithmEnum'
import { AlgorithmResponse, AlgorithmAttributes } from '../types/AlgorithmTypes'
import ConfigAlgorithm from '../constants/ConfigAlgorithm'
import ConstantConfigType from '../types/ConstantConfigType'
import _ from 'lodash'
import i18n from 'live/services/i18n'

export const defineAlgorithmDetails = (
    algorithmResponse: AlgorithmResponse,
    modelAlgorithmEnum: ModelAlgorithmEnum
): AlgorithmAttributes => {
    const updatedConstants = Object.keys(algorithmResponse.constants).reduce((preset, parameter) => {
        preset[parameter] = algorithmResponse.parameters?.[parameter]
            ? algorithmResponse.parameters[parameter].value
            : algorithmResponse.constants[parameter]

        return preset
    }, {})

    const constantsConvertedList = defineConstantsAlgorithm(updatedConstants)

    switch (modelAlgorithmEnum) {
        case ModelAlgorithmEnum.RIG_STATE_MODEL:
            return {
                ...algorithmResponse,
                name: ConfigAlgorithm.mainModel.name,
                visibleName: ConfigAlgorithm.mainModel.visibleName,
                description: ConfigAlgorithm.mainModel.description,
                constantsConvertedList: constantsConvertedList,
                isLegacy: false
            }
        case ModelAlgorithmEnum.LEGACY_MODEL:
            return {
                ...algorithmResponse,
                name: ConfigAlgorithm.legacy.name,
                visibleName: ConfigAlgorithm.legacy.visibleName,
                description: ConfigAlgorithm.legacy.description,
                constantsConvertedList: constantsConvertedList,
                isLegacy: true
            }
        case ModelAlgorithmEnum.SIMPLIFIED_MODEL:
            return {
                ...algorithmResponse,
                name: ConfigAlgorithm.simplfied.name,
                visibleName: ConfigAlgorithm.simplfied.visibleName,
                description: ConfigAlgorithm.simplfied.description,
                constantsConvertedList: constantsConvertedList,
                isLegacy: false
            }
    }
}

const defineConstantsAlgorithm = (constants: Record<string, string & number>): Array<ConstantConfigType> => {
    const constantConfigTypeArr: Array<ConstantConfigType> = []

    for (const key in constants) {
        if (constants.hasOwnProperty(key)) {
            constantConfigTypeArr.push({
                label: i18n(_.startCase(key)),
                unit: DEFAULT_UNITS[key] || '',
                value: constants[key],
                key: key
            })
        }
    }

    return constantConfigTypeArr
}

export const UNITS_M_LEGACY: Record<string, string> = {
    minQueuePeriod: 'm',
    maxQueuePeriod: 'm',
    timeout: 's',
    pipeLength: 'm',
    minimumFluidFlow: 'm3/s',
    connectionTimeout: 'ms',
    minimumRotatorySpeed: 'rpm',
    minimumWeightOnBit: 'N',
    minimumTorque: 'N.m',
    minimumStandpipePressure: 'Pa',
    minimumRateOfPenetration: 'm/s',
    bottomDepthTolerance: 'm',
    blockWeight: 'N',
    blockWeightToleranceThreshold: '',
    hookLoadRatioThreshold: '',
    movementThreshold: 'm',
    standLength: 'pipe(s)',
    maximumTimeToDetectRevertConnection: 's',
    minimumDrillStringRotarySpeed: 'rpm',
    minimumOperationDuration: 'ms',
    lowBitDepthThreshold: 'm',
    dataPeriod: 's',
    minimumSpeed: 'm/s',
    enableSSAFiltering: '',
    hookLoadWithoutBlockWeight: ''
}

export const UNITS_FT: Record<string, string> = {
    pipeLength: 'ft',
    standLength: 'pipe(s)',
    minimumFluidFlow: 'galUS/min',
    minimumRotatorySpeed: 'rpm',
    minimumWeightOnBit: 'klbf',
    minimumTorque: 'klbf.ft',
    minimumStandpipePressure: 'psi',
    minimumRateOfPenetration: 'ft/h',
    bottomDepthTolerance: 'ft',
    movementThreshold: 'ft',
    blockWeight: 'klbf',
    connectionTimeout: 'ms',
    blockWeightToleranceThreshold: '',
    hookLoadRatioThreshold: '',
    minimumOperationDuration: 'ms',
    lowBitDepthThreshold: 'ft',
    dataPeriod: 's',
    minimumSpeed: 'ft/s',
    enableSSAFiltering: '',
    hookLoadWithoutBlockWeight: ''
}

export const UNITS: Record<string, string> = {
    pipeLength: 'm',
    standLength: 'pipe(s)',
    minimumFluidFlow: 'galUS/min',
    minimumRotatorySpeed: 'rpm',
    minimumWeightOnBit: 'klbf',
    minimumTorque: 'N.m',
    minimumStandpipePressure: 'psi',
    minimumRateOfPenetration: 'm/h',
    bottomDepthTolerance: 'm',
    movementThreshold: 'm',
    blockWeight: 'klbf',
    connectionTimeout: 'ms',
    blockWeightToleranceThreshold: '',
    hookLoadRatioThreshold: '',
    minimumOperationDuration: 'ms',
    lowBitDepthThreshold: 'm',
    dataPeriod: 's',
    minimumSpeed: 'm/s',
    enableSSAFiltering: '',
    hookLoadWithoutBlockWeight: ''
}

export const DEFAULT_UNITS: Record<string, string> = {
    pipeLength: 'm',
    standLength: 'pipe(s)',
    minimumFluidFlow: 'm3/s',
    minimumRotatorySpeed: 'rad/s',
    minimumWeightOnBit: 'N',
    minimumTorque: 'N.m',
    minimumStandpipePressure: 'Pa',
    minimumRateOfPenetration: 'm/s',
    bottomDepthTolerance: 'm',
    movementThreshold: 'm',
    blockWeight: 'N',
    connectionTimeout: 'ms',
    blockWeightToleranceThreshold: '',
    hookLoadRatioThreshold: '',
    minimumOperationDuration: 'ms',
    lowBitDepthThreshold: 'm',
    dataPeriod: 's',
    minimumSpeed: 'm/s',
    enableSSAFiltering: '',
    hookLoadWithoutBlockWeight: ''
}

export const parameterUnits = {
    meter: UNITS,
    foot: UNITS_FT,
    default: DEFAULT_UNITS
}
